import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider,
} from '@mui/material';
import {
  BrandingWatermark,
  ArrowBack,
  Check,
  Warning,
  Error as ErrorIcon,
  Edit,
  Save,
} from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import toast from 'react-hot-toast';
import brandAPI, { BrandGuidelineData } from '../services/brandAPI';

const splitCsv = (value: string): string[] =>
  value
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

const joinCsv = (items?: string[]): string => (items && items.length > 0 ? items.join(', ') : '');

const extractForbiddenWords = (guideline?: BrandGuidelineData | null): string[] => {
  const restrictions = guideline?.content_restrictions;
  if (!restrictions) return [];
  if (Array.isArray(restrictions)) return restrictions.map(String);
  const keys = ['prohibited_terms', 'banned_words', 'forbidden_words', 'avoid'];
  for (const key of keys) {
    const value = restrictions[key];
    if (Array.isArray(value)) return value.map(String);
  }
  return [];
};

interface GuidelineForm {
  brand_name: string;
  tone_of_voice: string;
  brand_voice: string;
  keywords: string;
  hashtags: string;
  forbidden_words: string;
  compliance_rules: string;
  writing_style: string;
}

const emptyForm: GuidelineForm = {
  brand_name: '',
  tone_of_voice: '',
  brand_voice: 'professional',
  keywords: '',
  hashtags: '',
  forbidden_words: '',
  compliance_rules: '',
  writing_style: '',
};

export const BrandConsistencyPage: React.FC = () => {
  const navigate = useNavigate();
  const [content, setContent] = useState('');
  const [guideline, setGuideline] = useState<BrandGuidelineData | null>(null);
  const [form, setForm] = useState<GuidelineForm>(emptyForm);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState<{
    passed: boolean;
    issues: string[];
    suggestions: string[];
    score: number;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadGuidelines = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await brandAPI.getGuidelines();
      if (res.has_guidelines && res.guideline) {
        setGuideline(res.guideline);
        setForm({
          brand_name: res.guideline.brand_name || '',
          tone_of_voice: res.guideline.tone_of_voice || '',
          brand_voice: res.guideline.brand_voice || 'professional',
          keywords: joinCsv(res.guideline.keywords),
          hashtags: joinCsv(res.guideline.hashtags),
          forbidden_words: joinCsv(extractForbiddenWords(res.guideline)),
          compliance_rules: res.guideline.compliance_rules || '',
          writing_style: res.guideline.writing_style || '',
        });
      } else {
        setGuideline(null);
        setForm(emptyForm);
        setEditing(true);
      }
    } catch {
      setError('Failed to load brand guidelines. Make sure you are logged in.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGuidelines();
  }, []);

  const handleSaveGuidelines = async () => {
    if (!form.brand_name.trim()) {
      toast.error('Brand name is required');
      return;
    }

    setSaving(true);
    try {
      const saved = await brandAPI.saveGuidelines({
        brand_name: form.brand_name.trim(),
        brand_voice: form.brand_voice.trim() || undefined,
        tone_of_voice: form.tone_of_voice.trim() || undefined,
        keywords: splitCsv(form.keywords),
        hashtags: splitCsv(form.hashtags),
        writing_style: form.writing_style.trim() || undefined,
        content_restrictions: {
          prohibited_terms: splitCsv(form.forbidden_words),
        },
        compliance_rules: form.compliance_rules.trim() || undefined,
        is_active: true,
      });
      setGuideline(saved);
      setEditing(false);
      toast.success('Brand guidelines saved — they will apply to new post generation');
    } catch {
      toast.error('Failed to save brand guidelines');
    } finally {
      setSaving(false);
    }
  };

  const checkConsistency = async () => {
    if (!content.trim()) return;
    if (!guideline) {
      toast.error('Save brand guidelines first');
      return;
    }

    setChecking(true);
    setError(null);
    try {
      const res = await brandAPI.checkContent(content);
      setResult({
        passed: res.passed,
        issues: res.issues,
        suggestions: res.suggestions,
        score: res.score,
      });
    } catch (err: any) {
      const msg = err?.response?.data?.detail || 'Failed to check brand consistency';
      setError(msg);
      toast.error(msg);
    } finally {
      setChecking(false);
    }
  };

  const getScoreColor = (score: number): 'success' | 'warning' | 'error' => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  const forbiddenDisplay = extractForbiddenWords(guideline);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Button startIcon={<ArrowBack />} onClick={() => navigate('/dashboard')} sx={{ mr: 2 }}>
            Back to Dashboard
          </Button>
          <BrandingWatermark color="primary" sx={{ mr: 2, fontSize: 32 }} />
          <Typography variant="h4" component="h1">
            Brand Consistency
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Edit your brand voice here. Saved guidelines are injected automatically when you generate posts on{' '}
          <RouterLink to="/create-post">Create Post</RouterLink>.
        </Typography>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        <strong>How it affects generation:</strong> tone, keywords, forbidden words, and compliance footer from your
        saved guidelines are added to AI prompts for generate, regenerate hook/hashtags, and shorten-for-X.
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Content Review
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={8}
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Paste a draft post to check it against your saved brand guidelines..."
                  sx={{ mb: 2 }}
                />
                <Button
                  variant="contained"
                  startIcon={checking ? <CircularProgress size={16} /> : <Check />}
                  onClick={checkConsistency}
                  disabled={!content.trim() || checking || !guideline}
                >
                  {checking ? 'Checking...' : 'Check Consistency'}
                </Button>
                {!guideline && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Save brand guidelines on the right before running a check.
                  </Typography>
                )}
              </CardContent>
            </Card>

            {result && (
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Consistency Report</Typography>
                    <Chip
                      label={`Score: ${result.score}%`}
                      color={getScoreColor(result.score)}
                      variant="outlined"
                    />
                  </Box>

                  {result.issues.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="error" gutterBottom>
                        Violations
                      </Typography>
                      <List dense>
                        {result.issues.map((violation, index) => (
                          <ListItem key={index}>
                            <ErrorIcon color="error" sx={{ mr: 1 }} />
                            <ListItemText primary={violation} />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}

                  {result.suggestions.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" color="warning.main" gutterBottom>
                        Suggestions
                      </Typography>
                      <List dense>
                        {result.suggestions.map((suggestion, index) => (
                          <ListItem key={index}>
                            <Warning color="warning" sx={{ mr: 1 }} />
                            <ListItemText primary={suggestion} />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}

                  {result.passed && result.suggestions.length === 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                      <Check color="success" sx={{ mr: 1 }} />
                      <Typography color="success.main">Content meets brand guidelines!</Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            )}
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">Brand Guidelines</Typography>
                  {guideline && !editing && (
                    <Button size="small" startIcon={<Edit />} onClick={() => setEditing(true)}>
                      Edit
                    </Button>
                  )}
                </Box>

                {editing ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      label="Brand name"
                      value={form.brand_name}
                      onChange={(e) => setForm({ ...form, brand_name: e.target.value })}
                      required
                      size="small"
                    />
                    <TextField
                      label="Tone of voice"
                      value={form.tone_of_voice}
                      onChange={(e) => setForm({ ...form, tone_of_voice: e.target.value })}
                      placeholder="Expert, helpful, and trustworthy"
                      size="small"
                    />
                    <TextField
                      label="Brand voice style"
                      value={form.brand_voice}
                      onChange={(e) => setForm({ ...form, brand_voice: e.target.value })}
                      placeholder="professional"
                      size="small"
                    />
                    <TextField
                      label="Preferred keywords (comma-separated)"
                      value={form.keywords}
                      onChange={(e) => setForm({ ...form, keywords: e.target.value })}
                      placeholder="innovation, trust"
                      size="small"
                    />
                    <TextField
                      label="Brand hashtags (comma-separated)"
                      value={form.hashtags}
                      onChange={(e) => setForm({ ...form, hashtags: e.target.value })}
                      placeholder="#YourBrand, #Innovation"
                      size="small"
                    />
                    <TextField
                      label="Forbidden words (comma-separated)"
                      value={form.forbidden_words}
                      onChange={(e) => setForm({ ...form, forbidden_words: e.target.value })}
                      placeholder="cheap, free, guaranteed"
                      size="small"
                      helperText="AI will avoid these when generating posts"
                    />
                    <TextField
                      label="Writing style"
                      value={form.writing_style}
                      onChange={(e) => setForm({ ...form, writing_style: e.target.value })}
                      placeholder="Concise and factual"
                      size="small"
                    />
                    <TextField
                      label="Compliance footer"
                      value={form.compliance_rules}
                      onChange={(e) => setForm({ ...form, compliance_rules: e.target.value })}
                      multiline
                      rows={3}
                      placeholder="Not financial advice. Past performance..."
                      size="small"
                      helperText="Appended automatically to generated posts"
                    />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="contained"
                        startIcon={saving ? <CircularProgress size={14} /> : <Save />}
                        onClick={handleSaveGuidelines}
                        disabled={saving}
                        fullWidth
                      >
                        Save guidelines
                      </Button>
                      {guideline && (
                        <Button variant="outlined" onClick={() => setEditing(false)} disabled={saving}>
                          Cancel
                        </Button>
                      )}
                    </Box>
                  </Box>
                ) : guideline ? (
                  <List dense>
                    <ListItem>
                      <ListItemText primary="Brand" secondary={guideline.brand_name} />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Brand Voice"
                        secondary={guideline.tone_of_voice || guideline.brand_voice || '—'}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Preferred Tone" secondary={guideline.brand_voice || '—'} />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Keywords"
                        secondary={joinCsv(guideline.keywords) || '—'}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Brand Hashtags"
                        secondary={joinCsv(guideline.hashtags) || '—'}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Forbidden Words"
                        secondary={forbiddenDisplay.join(', ') || '—'}
                      />
                    </ListItem>
                    {guideline.compliance_rules && (
                      <>
                        <Divider sx={{ my: 1 }} />
                        <ListItem>
                          <ListItemText
                            primary="Compliance footer"
                            secondary={guideline.compliance_rules}
                          />
                        </ListItem>
                      </>
                    )}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No guidelines saved yet. Fill in the form above and click Save.
                  </Typography>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Post generation impact
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="Create Post"
                      secondary="AI prompts include your tone, keywords, and forbidden words"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Regenerate hook / hashtags"
                      secondary="Uses the same brand voice rules"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Shorten for X"
                      secondary="Shortened tweets respect brand restrictions"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Compliance footer"
                      secondary="Added to the end of generated content automatically"
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Container>
  );
};

export default BrandConsistencyPage;
